import { useEffect, useState } from 'react';
import { useSession } from '@/app/providers/session-provider';
import { storeSessionUser } from '@/app/services/session.service';
import {
  getWorkspaceSettings,
  updateWorkspaceSettings,
} from '../services/workspace-settings.service';

const SETTINGS_FIELDS = [
  'displayName',
  'legalName',
  'contactEmail',
  'contactPhone',
  'website',
  'timezone',
  'locale',
  'defaultCurrency',
  'countryCode',
  'addressLine1',
  'addressLine2',
  'city',
  'postalCode',
];
const WORKSPACE_SETTINGS_AUTH_ERRORS = new Set([
  'WORKSPACE_SETTINGS_AUTH_REQUIRED',
  'WORKSPACE_SETTINGS_SESSION_EXPIRED',
  'WORKSPACE_SETTINGS_SESSION_REFRESH_REQUIRED',
]);

const EMPTY_SETTINGS_FORM = {
  name: '',
  displayName: '',
  legalName: '',
  contactEmail: '',
  contactPhone: '',
  website: '',
  timezone: '',
  locale: '',
  defaultCurrency: '',
  countryCode: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
};

function buildForm(workspace) {
  const settings = workspace?.settings || {};
  return {
    ...EMPTY_SETTINGS_FORM,
    name: workspace?.name || '',
    ...SETTINGS_FIELDS.reduce((form, field) => {
      form[field] = settings[field] || '';
      return form;
    }, {}),
  };
}

function toSessionTenant(workspace, previousTenant = {}) {
  return {
    ...previousTenant,
    id: workspace.tenantId || previousTenant?.id || '',
    name: workspace.name || previousTenant?.name || '',
    active: workspace.active !== false,
    slug: workspace.slug || previousTenant?.slug || '',
    planCode: workspace.planCode || previousTenant?.planCode || '',
    enabledModuleKeys: Array.isArray(workspace.enabledModuleKeys)
      ? workspace.enabledModuleKeys
      : previousTenant?.enabledModuleKeys || [],
    settings: {
      ...(previousTenant?.settings || {}),
      ...(workspace.settings || {}),
    },
  };
}

export function useWorkspaceSettingsData() {
  const { currentUser, setCurrentUser } = useSession();
  const [workspace, setWorkspace] = useState(null);
  const [form, setForm] = useState(EMPTY_SETTINGS_FORM);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [authRequired, setAuthRequired] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;

    async function loadSettings() {
      setIsLoading(true);
      const result = await getWorkspaceSettings();

      if (!active) {
        return;
      }

      if (!result.ok) {
        setWorkspace(null);
        setError(result.message || result.error || '');
        setAuthRequired(WORKSPACE_SETTINGS_AUTH_ERRORS.has(result.error));
        setUnavailable(result.error === 'WORKSPACE_SETTINGS_UNAVAILABLE');
        setIsLoading(false);
        return;
      }

      setWorkspace(result.data.workspace);
      setForm(buildForm(result.data.workspace));
      setError('');
      setAuthRequired(false);
      setUnavailable(false);
      setIsLoading(false);
    }

    loadSettings();

    return () => {
      active = false;
    };
  }, []);

  function updateField(field, value) {
    setForm((currentForm) => ({
      ...currentForm,
      [field]: value,
    }));
  }

  function syncSessionTenant(nextWorkspace) {
    if (!currentUser || !nextWorkspace) {
      return;
    }

    const nextUser = {
      ...currentUser,
      tenantId: nextWorkspace.tenantId || currentUser.tenantId,
      tenant: toSessionTenant(nextWorkspace, currentUser.tenant),
    };

    storeSessionUser(nextUser);
    setCurrentUser(nextUser);
  }

  async function saveSettings() {
    setError('');
    setSuccess('');
    setIsSaving(true);

    const result = await updateWorkspaceSettings({
      name: form.name.trim(),
      settings: SETTINGS_FIELDS.reduce((payload, field) => {
        payload[field] = form[field];
        return payload;
      }, {}),
    });

    setIsSaving(false);

    if (!result.ok) {
      setError(result.message || result.error || '');
      setAuthRequired(WORKSPACE_SETTINGS_AUTH_ERRORS.has(result.error));
      setUnavailable(result.error === 'WORKSPACE_SETTINGS_UPDATE_UNAVAILABLE');
      return false;
    }

    setWorkspace(result.data.workspace);
    setForm(buildForm(result.data.workspace));
    setError('');
    setAuthRequired(false);
    setUnavailable(false);
    setSuccess(result.message || 'Impostazioni workspace aggiornate.');
    syncSessionTenant(result.data.workspace);
    return true;
  }

  return {
    workspace,
    form,
    isLoading,
    isSaving,
    error,
    success,
    authRequired,
    unavailable,
    updateField,
    saveSettings,
  };
}
